class MantysMegaShopTabs extends HTMLElement {
  connectedCallback() {
    if (this.isInitialized) return;

    if (!this.pendingObserver) {
      this.pendingObserver = new MutationObserver(() => this.scheduleInitialization());
      this.pendingObserver.observe(this, { childList: true, subtree: true });
    }

    this.scheduleInitialization();
  }

  scheduleInitialization() {
    if (this.isInitialized || this.initializationFrame) return;

    this.initializationFrame = requestAnimationFrame(() => {
      this.initializationFrame = null;
      if (!this.initialize()) return;

      this.pendingObserver?.disconnect();
      this.pendingObserver = null;
    });
  }

  initialize() {
    this.triggers = [...this.querySelectorAll('[data-mega-tab-trigger]')];
    this.panels = [...this.querySelectorAll('[data-mega-tab-panel]')];
    if (!this.triggers.length || this.triggers.length !== this.panels.length) return false;

    this.isInitialized = true;
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    const activePanel = this.querySelector('[data-mega-tab-panel]:not([hidden])');
    this.menu = this.closest('.hdt-mega-shop--tabbed');
    this.menuDetails = this.closest('details');

    this.triggers.forEach((trigger, index) => {
      trigger.addEventListener('click', () => this.selectTab(index), { signal });
      trigger.addEventListener('keydown', (event) => this.handleKeydown(event, index), { signal });
    });

    this.hydratePanelProducts(activePanel);
    this.scheduleHeightUpdate();
    this.menuDetails?.addEventListener('toggle', () => this.syncMenuHeight(), { signal });
    this.menuDetails?.addEventListener('mouseenter', () => this.syncMenuHeight(), { signal });
    this.menu?.addEventListener('transitionend', (event) => {
      if (event.target === this.menu) this.scheduleHeightUpdate();
    }, { signal });
    window.addEventListener('resize', () => this.handleViewportChange(), { signal, passive: true });
    window.addEventListener('scroll', () => this.handleViewportChange(), { signal, passive: true });

    return true;
  }

  disconnectedCallback() {
    this.pendingObserver?.disconnect();
    this.abortController?.abort();
    if (this.initializationFrame) cancelAnimationFrame(this.initializationFrame);
    if (this.heightFrame) cancelAnimationFrame(this.heightFrame);
    if (this.heightTimeout) clearTimeout(this.heightTimeout);
    this.heightFrame = null;
    this.heightTimeout = null;
    this.initializationFrame = null;
    this.pendingObserver = null;
    this.isInitialized = false;
  }

  handleKeydown(event, currentIndex) {
    const lastIndex = this.triggers.length - 1;
    let nextIndex;

    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = lastIndex;
    } else {
      return;
    }

    event.preventDefault();
    this.selectTab(nextIndex, true);
  }

  selectTab(index, moveFocus = false) {
    const activeTrigger = this.triggers[index];
    const activePanel = this.panels[index];
    if (!activeTrigger || !activePanel) return;

    this.triggers.forEach((trigger, triggerIndex) => {
      const isActive = triggerIndex === index;
      trigger.classList.toggle('is-active', isActive);
      trigger.setAttribute('aria-selected', String(isActive));
      trigger.tabIndex = isActive ? 0 : -1;
    });

    this.panels.forEach((panel, panelIndex) => {
      const isActive = panelIndex === index;
      panel.hidden = !isActive;
      panel.classList.toggle('is-active', isActive);
      panel.classList.remove('is-entering');
    });

    activePanel.classList.add('is-entering');
    if (moveFocus) activeTrigger.focus();
    this.hydratePanelProducts(activePanel);
    this.scheduleHeightUpdate();
  }

  hydratePanelProducts(panel) {
    const template = panel?.querySelector('[data-mega-products-template]');
    const mount = panel?.querySelector('[data-mega-products-mount]');
    if (!template || !mount) return;

    mount.append(template.content.cloneNode(true));
    template.remove();
    mount.dispatchEvent(new CustomEvent('products:update', { bubbles: true }));
  }

  scheduleHeightUpdate() {
    if (this.heightFrame) return;

    this.heightFrame = requestAnimationFrame(() => {
      this.heightFrame = null;
      if (!this.menu) return;

      const menuTop = Math.max(0, this.menu.getBoundingClientRect().top);
      const availableHeight = Math.max(0, window.innerHeight - menuTop);
      this.menu.style.setProperty('--mantys-mega-menu-height', `${availableHeight}px`);
    });
  }

  handleViewportChange() {
    if (this.menuDetails?.open) this.scheduleHeightUpdate();
  }

  syncMenuHeight() {
    this.scheduleHeightUpdate();
    if (this.heightTimeout) clearTimeout(this.heightTimeout);
    this.heightTimeout = setTimeout(() => this.scheduleHeightUpdate(), 350);
  }
}

if (!customElements.get('mantys-mega-shop-tabs')) {
  customElements.define('mantys-mega-shop-tabs', MantysMegaShopTabs);
}
